import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTask, useTags } from "../../../../../hooks/query";
import { useProject } from "../../../../../hooks";
import moment from "moment";

interface NewSubTaskInlineFormData {
    projectId: string | { value: string; label: string };
    eventName: string;
    startDateTime: string;
    endDateTime: string;
    taskStatus: string;
    description: string;
    assignee: string | { value: string; label: string };
    color: string;
}

interface UseNewSubTaskProps {
    selectedDate: Date;
    onFormStateChange?: (isValid: boolean) => void;
}

export function useNewSubTask({ selectedDate, onFormStateChange }: UseNewSubTaskProps) {
    const { useCreate, useListByProjectId } = useTask();
    const createTaskSvc = useCreate();
    const listProjectTasksSvc = useListByProjectId();
    const { project } = useProject();

    const { useAssignTags } = useTags();
    const assignTagsSvc = useAssignTags();

    const [jobExecutionTaskId, setJobExecutionTaskId] = useState<string | null>(null);

    const currentProjectValue = project?.id
        ? {
              value: project.id,
              label: project.name || project.Name || project.slug || "Current project",
          }
        : "";

    const methods = useForm<NewSubTaskInlineFormData>({
        defaultValues: {
            projectId: currentProjectValue,
            eventName: "",
            startDateTime: selectedDate.toISOString().slice(0, 16),
            endDateTime: selectedDate.toISOString().slice(0, 16),
            taskStatus: "",
            description: "",
            assignee: "",
            color: "purple",
        },
        mode: "onChange",
    });

    const findJobExecutionTaskId = async (projectId: string) => {
        try {
            const response = await listProjectTasksSvc.mutateAsync({ ProjectId: projectId });
            const tasks = response || [];

            const jobExecutionTask = tasks.find(
                (task: any) => task.Name && task.Name.toLowerCase().includes("job execution"),
            );

            if (jobExecutionTask) {
                setJobExecutionTaskId(jobExecutionTask.Id);
                return jobExecutionTask.Id;
            }

            console.warn("Job Execution task not found for project:", projectId);
            return null;
        } catch (error) {
            console.error("Error fetching project tasks:", error);
            return null;
        }
    };

    useEffect(() => {
        methods.reset({
            projectId: currentProjectValue,
            eventName: "",
            startDateTime: selectedDate.toISOString().slice(0, 16),
            endDateTime: selectedDate.toISOString().slice(0, 16),
            taskStatus: "",
            description: "",
            assignee: "",
            color: "purple",
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate, project?.id]);

    useEffect(() => {
        if (onFormStateChange) {
            onFormStateChange(methods.formState.isValid);
        }
    }, [methods.formState.isValid, onFormStateChange]);

    const watchedProjectId = methods.watch("projectId");
    useEffect(() => {
        if (watchedProjectId) {
            const projectId =
                typeof watchedProjectId === "object" ? watchedProjectId.value : watchedProjectId;
            void findJobExecutionTaskId(projectId);
        } else {
            setJobExecutionTaskId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedProjectId]);

    const handleSubmit = async (data: NewSubTaskInlineFormData, onSubmit: (data: any) => void) => {
        try {
            if (jobExecutionTaskId && data.projectId) {
                const projectId =
                    typeof data.projectId === "object" ? data.projectId.value : data.projectId;

                const assigneeId = data.assignee
                    ? typeof data.assignee === "object"
                        ? data.assignee.value
                        : data.assignee
                    : null;

                const subtaskParams = {
                    ProjectId: projectId,
                    ParentTaskId: jobExecutionTaskId,
                    Name: data.eventName,
                    Status: data.taskStatus,
                    SortOrder: 0,
                    Description: data.description || data.eventName,
                    ExecutionDate: moment(data.startDateTime).utc().format("YYYY-MM-DD HH:mm:ss"),
                    ...(assigneeId && { AssigneeId: assigneeId }),
                };

                const createdTask = await createTaskSvc.mutateAsync(subtaskParams);

                if (createdTask && data.color) {
                    try {
                        await assignTagsSvc.mutateAsync({
                            EntityType: "task",
                            EntityId: createdTask.Id || createdTask.id,
                            Tags: { Color: data.color },
                        });
                    } catch (tagError) {
                        console.warn("Failed to assign color tag:", tagError);
                    }
                }
            }

            onSubmit(data);
            methods.reset({
                projectId: currentProjectValue,
                eventName: "",
                startDateTime: selectedDate.toISOString().slice(0, 16),
                endDateTime: selectedDate.toISOString().slice(0, 16),
                taskStatus: "",
                description: "",
                assignee: "",
                color: "purple",
            });
        } catch (error) {
            console.error("Error creating subtask:", error);
            onSubmit(data);
            methods.reset();
        }
    };

    return {
        methods,
        handleSubmit,
        jobExecutionTaskId,
        isLoading:
            createTaskSvc.isPending || assignTagsSvc.isPending || methods.formState.isSubmitting,
    };
}
